import { ServerError } from '@apollo/client/errors'
import { toast } from 'react-toastify'

import {
  isLikelyAuthInfrastructureFailure,
  isLoginServiceUnavailableError,
} from '@/auth/loginInfrastructureErrors'
import { env } from '@/config/env'
import i18n from '@/translations'

/** Singleton toast ids so downtime / recovery never stack duplicates. */
export const TOAST_ID_SERVICE_DOWN_GRAPHQL = 'communo-service-down-graphql'
export const TOAST_ID_SERVICE_DOWN_MERCURE = 'communo-service-down-mercure'
export const TOAST_ID_SERVICE_BACK_GRAPHQL = 'communo-service-back-graphql'
export const TOAST_ID_SERVICE_BACK_MERCURE = 'communo-service-back-mercure'

export type ReachabilityProbeKind = 'graphql' | 'mercure'

const PHASE_DELAYS_MS = [5000, 15000, 30000, 60_000] as const

/** When healthy, probe occasionally so a new outage is detected without a GraphQL request (cache-only UI). */
const IDLE_WATCH_INTERVAL_MS = 60_000

/** Recovery toast: brief acknowledgment then dismiss (downtime toast stays until fixed). */
const SERVICE_BACK_ONLINE_TOAST_MS = 6000

type KindRuntimeState = {
  active: boolean
  timeoutId: ReturnType<typeof setTimeout> | undefined
  phaseIndex: number
  probesCompletedInPhase: number
}

function emptyKindState(): KindRuntimeState {
  return {
    active: false,
    timeoutId: undefined,
    phaseIndex: 0,
    probesCompletedInPhase: 0,
  }
}

const stateByKind: Record<ReachabilityProbeKind, KindRuntimeState> = {
  graphql: emptyKindState(),
  mercure: emptyKindState(),
}

const idleWatchIntervalByKind: Partial<
  Record<ReachabilityProbeKind, ReturnType<typeof setInterval>>
> = {}

function clearIdleWatch(kind: ReachabilityProbeKind): void {
  const id = idleWatchIntervalByKind[kind]
  if (id !== undefined) {
    clearInterval(id)
    delete idleWatchIntervalByKind[kind]
  }
}

function startIdleWatch(kind: ReachabilityProbeKind): void {
  if (kind === 'mercure' && mercureHubUrl() === null) {
    return
  }
  clearIdleWatch(kind)
  idleWatchIntervalByKind[kind] = setInterval(() => {
    void idleWatchTick(kind)
  }, IDLE_WATCH_INTERVAL_MS)
}

async function idleWatchTick(kind: ReachabilityProbeKind): Promise<void> {
  if (stateByKind[kind].active) {
    return
  }
  const probe = kind === 'graphql' ? probeGraphql : probeMercureHub
  const up = await probe()
  if (!up) {
    startMonitorKnownDown(kind)
  }
}

function downToastId(kind: ReachabilityProbeKind): string {
  return kind === 'graphql'
    ? TOAST_ID_SERVICE_DOWN_GRAPHQL
    : TOAST_ID_SERVICE_DOWN_MERCURE
}

function backToastId(kind: ReachabilityProbeKind): string {
  return kind === 'graphql'
    ? TOAST_ID_SERVICE_BACK_GRAPHQL
    : TOAST_ID_SERVICE_BACK_MERCURE
}

function graphqlUrl(): string {
  const base = String(env.API_URL ?? '').replace(/\/$/, '')
  return `${base}/graphql`
}

function mercureHubUrl(): string | null {
  const hubUrl = env.MERCURE_PUBLIC_URL
  return typeof hubUrl === 'string' && hubUrl.length > 0 ? hubUrl : null
}

/**
 * Any HTTP response except gateway / upstream outage counts as reachable
 * (e.g. 401 on GraphQL without a token still means the stack answered).
 */
function isTransportReachableResponse(res: Response): boolean {
  return res.status < 502 || res.status > 504
}

async function probeGraphql(): Promise<boolean> {
  try {
    const res = await fetch(graphqlUrl(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ query: '{ __typename }' }),
      credentials: 'omit',
    })
    return isTransportReachableResponse(res)
  } catch {
    return false
  }
}

async function probeMercureHub(): Promise<boolean> {
  const hubUrl = mercureHubUrl()
  if (!hubUrl) {
    return true
  }
  try {
    const res = await fetch(hubUrl, {
      method: 'GET',
      headers: { Accept: 'text/event-stream' },
      credentials: 'omit',
    })
    return isTransportReachableResponse(res)
  } catch {
    return false
  }
}

function clearScheduledTimeout(kind: ReachabilityProbeKind): void {
  const s = stateByKind[kind]
  if (s.timeoutId !== undefined) {
    clearTimeout(s.timeoutId)
    s.timeoutId = undefined
  }
}

function stopMonitor(kind: ReachabilityProbeKind): void {
  clearScheduledTimeout(kind)
  const s = stateByKind[kind]
  s.active = false
  s.phaseIndex = 0
  s.probesCompletedInPhase = 0
}

function showServiceDownToast(kind: ReachabilityProbeKind): void {
  const messageKey =
    kind === 'graphql'
      ? 'serviceReachability.apiDown'
      : 'serviceReachability.mercureDown'
  toast.error(i18n.t(messageKey), {
    toastId: downToastId(kind),
    autoClose: false,
    closeButton: false,
    closeOnClick: false,
    draggable: false,
    hideProgressBar: true,
  })
}

function showBackOnlineToast(kind: ReachabilityProbeKind): void {
  toast.success(i18n.t('serviceReachability.apiBackOnline'), {
    toastId: backToastId(kind),
    autoClose: SERVICE_BACK_ONLINE_TOAST_MS,
    closeOnClick: true,
  })
}

function onRecovered(kind: ReachabilityProbeKind): void {
  stopMonitor(kind)
  toast.dismiss(downToastId(kind))
  showBackOnlineToast(kind)
  startIdleWatch(kind)
}

/**
 * Max probes per phase before moving to the next delay tier. Last phase repeats forever.
 */
function maxProbesForPhase(phaseIndex: number): number | null {
  if (phaseIndex >= PHASE_DELAYS_MS.length - 1) {
    return null
  }
  return 3
}

function scheduleNextProbe(kind: ReachabilityProbeKind, delayMs: number): void {
  const s = stateByKind[kind]
  clearScheduledTimeout(kind)
  s.timeoutId = setTimeout(() => {
    s.timeoutId = undefined
    void evaluateProbe(kind, false)
  }, delayMs)
}

function afterFailedProbe(kind: ReachabilityProbeKind): void {
  const s = stateByKind[kind]
  if (!s.active) {
    return
  }
  s.probesCompletedInPhase += 1
  const max = maxProbesForPhase(s.phaseIndex)
  if (max !== null && s.probesCompletedInPhase >= max) {
    s.phaseIndex = Math.min(s.phaseIndex + 1, PHASE_DELAYS_MS.length - 1)
    s.probesCompletedInPhase = 0
  }
  const delayMs = PHASE_DELAYS_MS[s.phaseIndex]
  scheduleNextProbe(kind, delayMs)
}

/**
 * @param isInitialAttempt - First check after a trigger: if the service is up, stop silently (no toast).
 */
async function evaluateProbe(
  kind: ReachabilityProbeKind,
  isInitialAttempt: boolean
): Promise<void> {
  const s = stateByKind[kind]
  if (!s.active) {
    return
  }
  const probe = kind === 'graphql' ? probeGraphql : probeMercureHub
  const up = await probe()
  if (up) {
    if (isInitialAttempt) {
      stopMonitor(kind)
      startIdleWatch(kind)
    } else {
      onRecovered(kind)
    }
    return
  }
  if (isInitialAttempt) {
    showServiceDownToast(kind)
  }
  afterFailedProbe(kind)
}

/** Used after GraphQL/Mercure errors: confirm outage before showing the downtime toast. */
function startMonitorAfterSuspectedFailure(kind: ReachabilityProbeKind): void {
  const s = stateByKind[kind]
  if (s.active) {
    return
  }
  clearIdleWatch(kind)
  s.active = true
  s.phaseIndex = 0
  s.probesCompletedInPhase = 0
  void evaluateProbe(kind, true)
}

/**
 * Bootstrap already ran probeGraphql(); skip the duplicate immediate check and show downtime + backoff.
 */
function startMonitorKnownDown(kind: ReachabilityProbeKind): void {
  const s = stateByKind[kind]
  if (s.active) {
    return
  }
  clearIdleWatch(kind)
  s.active = true
  s.phaseIndex = 0
  s.probesCompletedInPhase = 0
  showServiceDownToast(kind)
  afterFailedProbe(kind)
}

/**
 * One-shot probe when the app shell loads (Layout mounted). Catches API down before any GraphQL error link runs.
 */
export async function runBootstrapApiReachabilityProbe(): Promise<void> {
  const up = await probeGraphql()
  if (!up) {
    startMonitorKnownDown('graphql')
  } else {
    startIdleWatch('graphql')
  }
  if (mercureHubUrl() !== null) {
    startIdleWatch('mercure')
  }
}

/**
 * GraphQL / API stack: show downtime toast, poll with escalating intervals until the API responds again.
 */
export function notifyServiceLikelyDown(): void {
  startMonitorAfterSuspectedFailure('graphql')
}

/**
 * Mercure hub: same UX pattern as API (dedicated singleton toast + probe).
 */
export function notifyMercureHubLikelyDown(): void {
  startMonitorAfterSuspectedFailure('mercure')
}

/**
 * Same monitoring as GraphQL outages: poll the API and show the `serviceReachability.apiBackOnline` toast.
 * Login infra errors (Keycloak timeout, bridge 502, fetch failures) all use this path so the UX stays one message.
 */
export function notifyServiceLikelyDownFromLoginError(e: unknown): void {
  if (
    !isLoginServiceUnavailableError(e) &&
    !isLikelyAuthInfrastructureFailure(e)
  ) {
    return
  }
  notifyServiceLikelyDown()
}

function readStatusCode(err: unknown): number | undefined {
  if (ServerError.is(err)) {
    return err.statusCode
  }
  if (!err || typeof err !== 'object') {
    return undefined
  }
  const code = (err as { statusCode?: unknown }).statusCode
  return typeof code === 'number' ? code : undefined
}

/**
 * Unwrap ApolloError (and similar) so HttpLink / upload link errors expose the underlying fetch error.
 */
export function unwrapApolloLinkNetworkError(err: unknown): unknown {
  if (
    err &&
    typeof err === 'object' &&
    'networkError' in err &&
    (err as { networkError: unknown }).networkError != null
  ) {
    return (err as { networkError: unknown }).networkError
  }
  return err
}

export function isHttpStatusInfrastructureFailure(status: number): boolean {
  if (status === 401 || status === 403 || status === 404) {
    return false
  }
  return status >= 500 && status < 600
}

function isGraphqlExtensionsInfrastructureFailure(
  extensions: unknown
): boolean {
  if (!extensions || typeof extensions !== 'object') {
    return false
  }
  const o = extensions as Record<string, unknown>
  const status = o.statusCode ?? o.status ?? o.httpStatus
  if (typeof status === 'number' && isHttpStatusInfrastructureFailure(status)) {
    return true
  }
  const code = String(o.code ?? '').toUpperCase()
  return (
    code === 'SERVICE_UNAVAILABLE' ||
    code === 'BAD_GATEWAY' ||
    code === 'GATEWAY_TIMEOUT'
  )
}

/**
 * True when the GraphQL transport failed in a way that suggests outage / network,
 * not a normal 401/403 application response.
 */
export function isApolloNetworkInfrastructureFailure(err: unknown): boolean {
  const networkError = unwrapApolloLinkNetworkError(err)
  if (networkError == null) {
    return false
  }
  if (networkError instanceof Error && networkError.name === 'AbortError') {
    return false
  }
  const status = readStatusCode(networkError)
  if (status !== undefined) {
    return isHttpStatusInfrastructureFailure(status)
  }
  if (networkError instanceof Error) {
    const msg = networkError.message.toLowerCase()
    return (
      msg.includes('failed to fetch') ||
      msg.includes('network error') ||
      msg.includes('load failed') ||
      msg.includes('networkrequestfailed') ||
      msg.includes('econnrefused') ||
      msg.includes('connection refused') ||
      msg.includes('timeout') ||
      msg.includes('aborted')
    )
  }
  return false
}

export function isApolloGraphqlResultInfrastructureFailure(
  graphQLErrors: readonly { extensions?: unknown }[] | undefined
): boolean {
  return (
    graphQLErrors?.some((g) =>
      isGraphqlExtensionsInfrastructureFailure(g.extensions)
    ) ?? false
  )
}
